import { useWindowScroll } from "@uidotdev/usehooks";
import { useEffect, useState } from "react";

type UseScrollVisibilityOptions = {
  /**
   * Show after scrolling this percentage of viewport height
   * @default 0.5 (50% of viewport)
   */
  showAfterVh?: number;
  /**
   * Hide when within this percentage of viewport height from bottom
   * @default 0.2 (20% of viewport)
   */
  hideBeforeBottomVh?: number;
  /**
   * Additional condition to control visibility
   * @default true
   */
  enabled?: boolean;
};

/**
 * Hook to control element visibility based on scroll position
 * Shows element after scrolling past a threshold and hides when near page bottom
 */
export const useScrollVisibility = ({
  showAfterVh = 0.5,
  hideBeforeBottomVh = 0.2,
  enabled = true,
}: UseScrollVisibilityOptions = {}) => {
  const [{ y }] = useWindowScroll();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsVisible(false);
      return;
    }

    const viewportHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollPosition = y ?? 0;

    // Show after scrolling past threshold
    const isPastShowThreshold = scrollPosition > viewportHeight * showAfterVh;

    // Hide when near bottom
    const distanceFromBottom = documentHeight - (scrollPosition + viewportHeight);
    const isNearBottom = distanceFromBottom < viewportHeight * hideBeforeBottomVh;

    setIsVisible(isPastShowThreshold && !isNearBottom);
  }, [y, showAfterVh, hideBeforeBottomVh, enabled]);

  return isVisible;
};
